import React, { useState, useEffect } from 'react';
import { 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  IconButton,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { fetchItemCategories, getDetailedCategories } from '../../utils/receiptsUtils';
import { useCategories } from '../../utils/CategoryContext';

const CategorySelector = ({
  highLevelCategory,
  detailedCategory,
  onSave,
  disabled = false,
}) => {
  const categories = useCategories();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedHighLevel, setSelectedHighLevel] = useState(highLevelCategory);
  const [selectedDetailed, setSelectedDetailed] = useState(detailedCategory);
  const [availableDetailedCategories, setAvailableDetailedCategories] = useState([]);
  const isMobile = useMediaQuery('(max-width:768px)');
  const isEmpty = !highLevelCategory;

  useEffect(() => {
    if (categories && selectedHighLevel) {
      const detailedCats = getDetailedCategories(categories, selectedHighLevel);
      setAvailableDetailedCategories(detailedCats);
    }
  }, [categories, selectedHighLevel]);

  const getCurrentEmoji = () => {
    if (!categories || !categories.categories) return '';
    const category = categories.categories.find(c => c.name === highLevelCategory);
    if (!category) return '';
    
    if (detailedCategory) {
      const subcategory = category.subcategories.find(s => s.name === detailedCategory);
      return subcategory ? subcategory.emoji : category.emoji;
    }
    return category.emoji;
  };

  const handleHighLevelChange = (event) => {
    setSelectedHighLevel(event.target.value);
    setSelectedDetailed(''); // Clear detailed category when high level changes
  };

  const handleDetailedChange = (event) => {
    setSelectedDetailed(event.target.value);
  };

  const handleSave = async () => {
    try {
      await onSave(selectedHighLevel, selectedDetailed);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving categories:', error);
    }
  };

  const handleCancel = () => {
    setSelectedHighLevel(highLevelCategory);
    setSelectedDetailed(detailedCategory);
    setIsEditing(false);
  };

  if (!categories) return null;

  return (
    <div className="flex items-center gap-1 w-full">
      {isEditing ? (
        <div className="flex items-start gap-2 p-1">
          <div className="flex flex-col gap-2 flex-grow">
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedHighLevel || ''}
                onChange={handleHighLevelChange}
                label="Category"
              >
                {categories.categories.map((category) => (
                  <MenuItem key={category.name} value={category.name}>
                    <span className="mr-2">{category.emoji}</span>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedHighLevel && (
              <FormControl size="small" fullWidth>
                <InputLabel>Subcategory</InputLabel>
                <Select
                  value={selectedDetailed || ''}
                  onChange={handleDetailedChange}
                  label="Subcategory"
                >
                  {availableDetailedCategories.map((subcategory) => (
                    <MenuItem key={subcategory.name} value={subcategory.name}>
                      <span className="mr-2">{subcategory.emoji}</span>
                      {subcategory.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <IconButton 
              onClick={handleSave} 
              size="small" 
              color="primary" 
              sx={{ 
                padding: isMobile ? '9px' : '4px',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                }
              }}
            >
              <CheckIcon sx={{ fontSize: isMobile ? '1.5rem' : '1rem' }} />
            </IconButton>
            <IconButton 
              onClick={handleCancel} 
              size="small" 
              color="error" 
              sx={{ 
                padding: isMobile ? '9px' : '4px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                }
              }}
            >
              <CloseIcon sx={{ fontSize: isMobile ? '1.5rem' : '1rem' }} />
            </IconButton>
          </div>
        </div>
      ) : (
        <div 
          className="flex items-center gap-1 group w-full min-h-[36px] cursor-pointer"
          onClick={() => !disabled && setIsEditing(true)}
        >
          <div className="flex items-center min-w-0 flex-grow">
            {isEmpty ? (
              <span className="text-gray-500 italic">Add category...</span>
            ) : (
              <>
                <span className="flex-shrink-0">{getCurrentEmoji()}</span>
                <span className="truncate ml-1">
                  {highLevelCategory}
                  {detailedCategory && (
                    <>
                      <span className="mx-1">›</span>
                      {detailedCategory}
                    </>
                  )}
                </span>
              </>
            )}
            {!disabled && (
              <Tooltip title="Edit categories">
                <IconButton 
                  size="small"
                  sx={{ 
                    padding: '8px',
                    marginLeft: '2px',
                    opacity: (isMobile || isEmpty) ? 0.7 : 0,
                    transition: 'opacity 0.2s',
                    flexShrink: 0,
                    '.group:hover &': {
                      opacity: 0.7,
                    },
                    '&:hover': {
                      opacity: '1 !important',
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorySelector;